import * as React from 'react'

import { mergeProps } from '@base-ui/react/merge-props'
import { useRender } from '@base-ui/react/use-render'

export interface IFilePickerActions {
  open(): void
}

interface IProps extends useRender.ComponentProps<'button'> {
  accept?: string
  multiple?: boolean
  capture?: 'user' | 'environment'
  actionsRef?: React.Ref<IFilePickerActions>
  onPick(files: File[]): void
}

/**
 * A trigger that opens the native file chooser. The `<input type="file">` is
 * hidden and owned here: it is cleared after every pick so the same file can be
 * picked twice in a row, and `onPick` only fires for a non-empty selection.
 * The trigger is a native `<button>`; compose a styled one with
 * `render={<Button variant="outline" />}`. Without `render` or `children` only
 * the input renders — open it through `actionsRef` from a trigger that cannot
 * host it, such as a menu item that unmounts before the pick lands.
 */
const FilePicker = React.forwardRef<HTMLButtonElement, IProps>(
  (
    {
      accept,
      multiple,
      capture,
      actionsRef,
      onPick,
      render,
      type,
      disabled,
      ...props
    },
    ref,
  ) => {
    const inputRef = React.useRef<HTMLInputElement>(null)
    const open = (): void => inputRef.current?.click()

    React.useImperativeHandle(actionsRef, () => ({ open }))

    const pick = (event: React.ChangeEvent<HTMLInputElement>): void => {
      const files = Array.from(event.currentTarget.files ?? [])
      event.currentTarget.value = ''
      if (files.length) {
        onPick(files)
      }
    }

    const trigger = useRender({
      defaultTagName: 'button',
      ref,
      render,
      props: mergeProps<'button'>(
        { type: type ?? 'button', disabled, onClick: open },
        props,
      ),
    })

    return (
      <>
        <input
          ref={inputRef}
          type="file"
          className="tw:hidden"
          accept={accept}
          multiple={multiple}
          capture={capture}
          disabled={disabled}
          onChange={pick}
        />
        {(render || props.children) && trigger}
      </>
    )
  },
)
FilePicker.displayName = 'FilePicker'

export default FilePicker
